import { SxProps, Theme } from "@mui/material";
import { button, inkfree, white } from "theme/colors";
import recipientsTokens from "./ApproversAndSigners.tokens";

const {
  accent,
  accentHover,
  ctaBg,
  ctaBgHover,
  menu,
  input,
  page,
  toolbar,
  tabs,
  addButton,
  levelHeader,
  row,
  select,
  security,
  delivery,
  delay,
  newLevelZone,
  levelDropTarget,
  empty,
  approvers,
  hostEmail,
  smsCredit,
} = recipientsTokens;

/** Popover chrome shared by the add / role / security menus. */
const menuPaper = {
  bgcolor: menu.bg,
  border: `1px solid ${menu.border}`,
  borderRadius: menu.radius,
  boxShadow: menu.shadow,
  p: menu.padding,
} as const;

/**
 * The design's underline input, applied to a `CustomAutoComplete` wrapper.
 * `CustomAutoComplete`'s own TextField styles set the input padding with
 * `!important`, so the design's padding has to match that weight.
 */
const underlineInput = (fontWeight: number, height: number, fontSize: string) =>
  ({
    "& .MuiInputBase-root": {
      height,
      borderRadius: input.radius,
      bgcolor: input.bg,
      borderBottom: input.underline,
      transition: "border-color .15s",
      "&:before, &:after": { display: "none" },
      "&:hover": { borderBottom: input.underline },
      "&.Mui-focused": { borderBottom: input.focusUnderline },
    },
    "& .MuiInputBase-input": {
      p: `${input.padding} !important`,
      height: "auto",
      fontSize,
      fontWeight,
      lineHeight: "normal",
      color: `${input.color} !important`,
      "&::placeholder": { color: input.placeholderColor, opacity: 1 },
    },
    "& .MuiAutocomplete-endAdornment": { right: 0 },
    "& .MuiFormHelperText-root": {
      ml: 0,
      mt: "2px",
      fontSize: "11px",
      lineHeight: 1.3,
    },
  }) as SxProps<Theme>;

/**
 * Boxed input treatment (signer name / email) — a bordered rounded box, per the
 * design's `boxed` field variant, in place of the underline above. Same
 * `CustomAutoComplete` wrapper, so the padding still needs `!important`.
 */
const boxedInput = (fontWeight: number, height: number, fontSize: string) =>
  ({
    "& .MuiInputBase-root": {
      height,
      borderRadius: input.boxedRadius,
      bgcolor: white,
      border: input.boxedBorder,
      transition: "border-color .15s",
      "&:before, &:after": { display: "none" },
      "&:hover": { borderColor: accent },
      "&.Mui-focused": { borderColor: accent },
    },
    "& .MuiInputBase-input": {
      p: input.boxedPadding,
      height: "auto",
      fontSize,
      fontWeight,
      lineHeight: "normal",
      color: `${input.color} !important`,
      "&::placeholder": { color: input.placeholderColor, opacity: 1 },
    },
    "& .MuiAutocomplete-endAdornment": { right: 6 },
    "& .MuiFormHelperText-root": {
      ml: 0,
      mt: "2px",
      fontSize: "11px",
      lineHeight: 1.3,
    },
  }) as SxProps<Theme>;

/** Small icon-only hit area that tints to the accent on hover. */
const hoverAccentButton = (size: number, height: number, radius: string) =>
  ({
    width: size,
    height,
    minWidth: 0,
    p: 0,
    borderRadius: radius,
    color: levelHeader.moveColor,
    "&:hover": { bgcolor: levelHeader.moveHoverBg, color: accent },
    "&:focus-visible": {
      outline: `2px solid ${inkfree.main}`,
      outlineOffset: "2px",
    },
  }) as const;

const inlineControlRing = (contentHeight: number) =>
  ({
    position: "relative",
    "&::after": {
      content: '""',
      position: "absolute",
      inset: `${(button.height - contentHeight - 6) / 2}px -3px`,
      borderRadius: "6px",
      pointerEvents: "none",
    },
    "&:focus-visible": { outline: "none" },
    "&:focus-visible::after": {
      outline: `2px solid ${inkfree.main}`,
      outlineOffset: 0,
    },
  }) as const;

const approversAndSignersStyles = {
  page: {
    bgcolor: page.bg,
    maxWidth: page.maxWidth,
    mx: "auto",
    p: page.padding,
    boxSizing: "border-box",
  } as SxProps<Theme>,

  // ---------------------------------------------------------------- toolbar

  toolbar: {
    display: "grid",
    gridTemplateColumns: {
      xs: "minmax(0,1fr)",
      md: "minmax(0,1fr) auto minmax(0,1fr)",
    },
    alignItems: "center",
    rowGap: toolbar.leftGap,
    mb: toolbar.marginBottom,
  } as SxProps<Theme>,

  toolbarLead: {
    display: "flex",
    alignItems: "center",
    gap: toolbar.leftGap,
    minWidth: 0,
  } as SxProps<Theme>,

  orderToggle: {
    display: "flex",
    alignItems: "center",
    gap: toolbar.leftGap,
    p: 0,
    minWidth: 0,
    textTransform: "none",
    lineHeight: "normal",
    "&:hover": { bgcolor: "transparent" },
    "&.Mui-disabled": { opacity: 0.55 },
    ...inlineControlRing(toolbar.checkboxSize),
  } as SxProps<Theme>,

  orderBox: (checked: boolean) =>
    ({
      width: toolbar.checkboxSize,
      height: toolbar.checkboxSize,
      borderRadius: toolbar.checkboxRadius,
      border: `${toolbar.checkboxBorderWidth} solid ${
        checked ? toolbar.checkboxBorderOn : toolbar.checkboxBorderOff
      }`,
      bgcolor: checked ? toolbar.checkboxBgOn : toolbar.checkboxBgOff,
      color: white,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxSizing: "border-box",
      flexShrink: 0,
    }) as SxProps<Theme>,

  orderTick: (checked: boolean) =>
    ({
      width: toolbar.checkboxTickSize,
      height: toolbar.checkboxTickSize,
      opacity: checked ? 1 : 0,
    }) as SxProps<Theme>,

  orderLabel: {
    fontSize: toolbar.labelFontSize,
    fontWeight: toolbar.labelFontWeight,
    color: toolbar.labelColor,
    lineHeight: "normal",
    whiteSpace: "nowrap",
  } as SxProps<Theme>,

  orderInfoIcon: {
    width: toolbar.infoIconSize,
    height: toolbar.infoIconSize,
    color: toolbar.infoIconColor,
    flexShrink: 0,
  } as SxProps<Theme>,

  /** Strips `InfoHelper`'s 44px hit area so the glyph sits tight to the label. */
  orderInfoButton: {
    p: 0,
    minWidth: "unset",
    minHeight: "unset",
    cursor: "help",
    "&:hover": { bgcolor: "transparent" },
  } as SxProps<Theme>,

  orderHint: {
    m: toolbar.hintMargin,
    fontSize: toolbar.hintFontSize,
    color: toolbar.hintColor,
    lineHeight: 1.5,
  } as SxProps<Theme>,

  // ------------------------------------------------------------------- tabs

  tabs: {
    display: "flex",
    justifyContent: "center",
    gap: tabs.gap,
  } as SxProps<Theme>,

  tab: (active: boolean) =>
    ({
      display: "flex",
      alignItems: "center",
      gap: tabs.itemGap,
      minWidth: 0,
      p: tabs.padding,
      borderRadius: 0,
      borderBottom: `${tabs.indicatorWidth} solid ${
        active ? tabs.indicatorActive : tabs.indicatorIdle
      }`,
      color: active ? tabs.colorActive : tabs.colorIdle,
      fontSize: tabs.labelFontSize,
      fontWeight: tabs.labelFontWeight,
      lineHeight: "normal",
      textTransform: "none",
      whiteSpace: "nowrap",
      "&:hover": { bgcolor: "transparent" },
      "&.Mui-disabled": { color: tabs.colorIdle, opacity: 0.55 },
    }) as SxProps<Theme>,

  tabIcon: {
    width: tabs.iconSize,
    height: tabs.iconSize,
    flexShrink: 0,
  } as SxProps<Theme>,

  tabCount: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: tabs.countMinWidth,
    height: tabs.countHeight,
    p: tabs.countPadding,
    borderRadius: tabs.countRadius,
    bgcolor: tabs.countBg,
    color: tabs.countColor,
    fontSize: tabs.countFontSize,
    fontWeight: tabs.countFontWeight,
    lineHeight: "normal",
    boxSizing: "border-box",
    flexShrink: 0,
  } as SxProps<Theme>,

  // -------------------------------------------------------------- add button

  toolbarActions: {
    display: "flex",
    justifyContent: { xs: "flex-start", md: "flex-end" },
    gap: toolbar.actionsGap,
  } as SxProps<Theme>,

  addGroup: {
    position: "relative",
    display: "flex",
    alignItems: "stretch",
    height: addButton.height,
    borderRadius: addButton.radius,
  } as SxProps<Theme>,

  addMain: {
    display: "flex",
    alignItems: "center",
    gap: addButton.gap,
    minWidth: 0,
    height: "100%",
    p: addButton.padding,
    background: addButton.bg,
    borderRadius: `${addButton.radius} 0 0 ${addButton.radius}`,
    color: addButton.color,
    fontSize: addButton.fontSize,
    fontWeight: addButton.fontWeight,
    lineHeight: "normal",
    textTransform: "none",
    whiteSpace: "nowrap",
    boxShadow: "none",
    "&:hover": { background: addButton.hoverBg, boxShadow: "none" },
    "&.Mui-disabled": {
      background: addButton.bg,
      color: addButton.color,
      opacity: 0.6,
    },
  } as SxProps<Theme>,

  addDivider: {
    width: "1px",
    bgcolor: addButton.dividerColor,
    flexShrink: 0,
  } as SxProps<Theme>,

  addCaret: {
    width: addButton.caretWidth,
    minWidth: addButton.caretWidth,
    height: "100%",
    p: 0,
    background: addButton.bg,
    borderRadius: `0 ${addButton.radius} ${addButton.radius} 0`,
    color: addButton.color,
    boxShadow: "none",
    "&:hover": { background: addButton.hoverBg, boxShadow: "none" },
    "&:focus-visible": {
      outline: `2px solid ${inkfree.main}`,
      outlineOffset: "2px",
    },
  } as SxProps<Theme>,

  addIcon: {
    width: addButton.iconSize,
    height: addButton.iconSize,
  } as SxProps<Theme>,

  addCaretIcon: {
    width: addButton.caretIconSize,
    height: addButton.caretIconSize,
  } as SxProps<Theme>,

  // -------------------------------------------------------------- menus

  menuPaper: (width: number) =>
    ({ ...menuPaper, width, mt: 0.75 }) as SxProps<Theme>,

  menuItem: {
    display: "block",
    p: menu.itemPadding,
    borderRadius: menu.itemRadius,
    "&:hover": { bgcolor: menu.itemHoverBg },
    "&.Mui-selected, &.Mui-selected:hover": { bgcolor: menu.itemHoverBg },
  } as SxProps<Theme>,

  menuItemTitle: {
    fontSize: menu.titleFontSize,
    fontWeight: menu.titleFontWeight,
    color: menu.titleColor,
    lineHeight: "normal",
  } as SxProps<Theme>,

  menuItemSub: {
    mt: "2px",
    fontSize: menu.subFontSize,
    color: menu.subColor,
    lineHeight: 1.35,
    // Captions can be long (delivery modes); wrap instead of clipping under
    // the MenuItem's default `white-space: nowrap`.
    whiteSpace: "normal",
  } as SxProps<Theme>,

  // ------------------------------------------------------------ level header

  levelHeader: {
    display: "flex",
    alignItems: "center",
    gap: levelHeader.gap,
    m: levelHeader.margin,
    width: "100%",
  } as SxProps<Theme>,

  levelNum: (color: string) =>
    ({
      width: levelHeader.numSize,
      height: levelHeader.numSize,
      borderRadius: "50%",
      bgcolor: color,
      color: levelHeader.numColor,
      fontSize: levelHeader.numFontSize,
      fontWeight: levelHeader.numFontWeight,
      lineHeight: "normal",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    }) as SxProps<Theme>,

  levelLabel: {
    fontSize: levelHeader.labelFontSize,
    fontWeight: levelHeader.labelFontWeight,
    letterSpacing: levelHeader.labelLetterSpacing,
    color: levelHeader.labelColor,
    lineHeight: "normal",
    whiteSpace: "nowrap",
  } as SxProps<Theme>,

  levelSub: {
    fontSize: levelHeader.subFontSize,
    color: levelHeader.subColor,
    lineHeight: "normal",
    whiteSpace: "nowrap",
  } as SxProps<Theme>,

  levelRule: {
    flex: 1,
    minWidth: 0,
    height: "1px",
    bgcolor: levelHeader.ruleColor,
  } as SxProps<Theme>,

  levelMoveGroup: {
    display: "flex",
    alignItems: "center",
    gap: "2px",
    mr: "4px",
    flexShrink: 0,
  } as SxProps<Theme>,

  levelMove: (disabled: boolean) =>
    ({
      ...hoverAccentButton(
        levelHeader.moveWidth,
        levelHeader.moveHeight,
        levelHeader.moveRadius,
      ),
      opacity: disabled ? levelHeader.moveDisabledOpacity : 1,
    }) as SxProps<Theme>,

  levelMoveIcon: {
    width: levelHeader.moveIconSize,
    height: levelHeader.moveIconSize,
  } as SxProps<Theme>,

  levelAdd: {
    display: "flex",
    alignItems: "center",
    gap: levelHeader.addGap,
    minWidth: 0,
    height: levelHeader.addHeight,
    p: levelHeader.addPadding,
    borderRadius: levelHeader.addRadius,
    border: levelHeader.addBorder,
    bgcolor: row.bg,
    color: levelHeader.addColor,
    fontSize: levelHeader.addFontSize,
    fontWeight: levelHeader.addFontWeight,
    lineHeight: "normal",
    textTransform: "none",
    whiteSpace: "nowrap",
    flexShrink: 0,
    "&:hover": { bgcolor: row.bg, borderColor: accent, color: accent },
  } as SxProps<Theme>,

  levelAddIcon: {
    width: levelHeader.addIconSize,
    height: levelHeader.addIconSize,
  } as SxProps<Theme>,

  // -------------------------------------------------------------- signer row

  rowWrapper: {
    display: "flex",
    flexDirection: "column",
    mb: row.marginBottom,
    width: "100%",
  } as SxProps<Theme>,

  rowCard: (dragging: boolean) =>
    ({
      display: "flex",
      alignItems: "stretch",
      border: `1px solid ${row.border}`,
      borderRadius: row.radius,
      bgcolor: row.bg,
      boxShadow: row.shadow,
      opacity: dragging ? 0 : 1,
      position: "relative",
      overflow: "visible",
    }) as SxProps<Theme>,

  rowBar: (color: string) =>
    ({
      width: row.barWidth,
      alignSelf: "stretch",
      borderRadius: row.barRadius,
      bgcolor: color,
      flexShrink: 0,
    }) as SxProps<Theme>,

  /** Column beside the bar: the input/select row, then the "Deliver via" line. */
  rowContent: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
  } as SxProps<Theme>,

  /** The horizontal input + select rail (everything except the channel line). */
  rowTop: {
    display: "flex",
    alignItems: "center",
    gap: row.gap,
    minHeight: row.minHeight,
    pl: `${row.gap}`,
    pr: row.paddingRight,
  } as SxProps<Theme>,

  rowDragHandle: (enabled: boolean) =>
    ({
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: row.dragWidth,
      height: row.dragHeight,
      p: 0,
      minWidth: 0,
      borderRadius: 0,
      color: row.dragIconColor,
      flexShrink: 0,
      cursor: enabled ? "grab" : "default",
      "&:hover": { bgcolor: "transparent" },
    }) as SxProps<Theme>,

  rowDragIcon: {
    width: row.dragIconSize,
    height: row.dragIconSize,
  } as SxProps<Theme>,

  nameField: {
    flex: input.nameFlex,
    minWidth: 0,
    ...(boxedInput(
      input.nameFontWeight,
      input.boxedHeight,
      input.nameFontSize,
    ) as object),
  } as SxProps<Theme>,

  emailField: {
    flex: input.emailFlex,
    minWidth: 0,
    ...(boxedInput(
      input.emailFontWeight,
      input.boxedHeight,
      input.emailFontSize,
    ) as object),
  } as SxProps<Theme>,

  /**
   * The name/email contact-suggestion dropdown: its anchor field is a narrow
   * flex column, far too tight for a full name + email pair, so the popped-up
   * list gets the same chrome as the role/security/delivery menus, sized for
   * its own content instead of inheriting the field's width.
   */
  contactSuggestionPaper: {
    ...menuPaper,
    width: menu.contactWidth,
    mt: 0.75,
  } as SxProps<Theme>,

  contactSuggestionItem: {
    display: "flex",
    alignItems: "center",
    p: menu.itemPadding,
    borderRadius: menu.itemRadius,
    cursor: "pointer",
    '&[aria-selected="true"], &:hover': { bgcolor: menu.itemHoverBg },
  } as SxProps<Theme>,

  contactSuggestionText: {
    minWidth: 0,
    flex: 1,
  } as SxProps<Theme>,

  contactSuggestionName: {
    fontSize: menu.titleFontSize,
    fontWeight: menu.titleFontWeight,
    color: menu.titleColor,
    lineHeight: "normal",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  } as SxProps<Theme>,

  contactSuggestionEmail: {
    mt: "1px",
    fontSize: menu.subFontSize,
    color: menu.subColor,
    lineHeight: 1.35,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  } as SxProps<Theme>,

  /** Approver-group name — same underline field, heavier weight per the design. */
  groupNameField: {
    flex: input.nameFlex,
    minWidth: 0,
    ...(underlineInput(
      approvers.groupNameFontWeight,
      input.height,
      input.nameFontSize,
    ) as object),
  } as SxProps<Theme>,

  /** Same treatment, shorter, for approver-group member rows. */
  memberField: (bold: boolean) =>
    ({
      flex: bold ? input.nameFlex : input.emailFlex,
      minWidth: 0,
      ...(underlineInput(
        bold ? input.nameFontWeight : input.emailFontWeight,
        input.memberHeight,
        input.memberFontSize,
      ) as object),
    }) as SxProps<Theme>,

  // ------------------------------------------------------- role / security

  select: {
    display: "flex",
    alignItems: "center",
    gap: select.gap,
    minWidth: 0,
    height: select.height,
    p: select.padding,
    border: select.border,
    borderRadius: select.radius,
    bgcolor: select.bg,
    color: select.color,
    fontSize: select.fontSize,
    fontWeight: select.fontWeight,
    lineHeight: "normal",
    textTransform: "none",
    whiteSpace: "nowrap",
    flexShrink: 0,
    "&:hover": { bgcolor: select.bg, borderColor: select.hoverBorder },
  } as SxProps<Theme>,

  selectCaret: {
    width: select.caretSize,
    height: select.caretSize,
    color: select.caretColor,
    flexShrink: 0,
  } as SxProps<Theme>,

  selectShield: (secured: boolean) =>
    ({
      width: select.shieldSize,
      height: select.shieldSize,
      color: secured ? accent : select.shieldIdleColor,
      flexShrink: 0,
    }) as SxProps<Theme>,

  /** Pen (signer) / eye (CC) icon on the role trigger. */
  selectRoleIcon: (cc: boolean) =>
    ({
      width: select.roleIconSize,
      height: select.roleIconSize,
      color: cc ? select.roleIconCcColor : select.roleIconSignerColor,
      flexShrink: 0,
    }) as SxProps<Theme>,

  /** Link (send a link) / monitor (in person) icon on the delivery-mode trigger. */
  selectDeliveryIcon: {
    width: select.deliveryIconSize,
    height: select.deliveryIconSize,
    color: select.deliveryIconColor,
    flexShrink: 0,
  } as SxProps<Theme>,

  // ------------------------------------------------------- in-row level move

  rowLevelMoveGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    flexShrink: 0,
  } as SxProps<Theme>,

  rowLevelMove: hoverAccentButton(
    row.levelMoveWidth,
    row.levelMoveHeight,
    row.levelMoveRadius,
  ) as SxProps<Theme>,

  rowLevelMoveIcon: {
    width: row.levelMoveIconSize,
    height: row.levelMoveIconSize,
  } as SxProps<Theme>,

  rowDelete: {
    width: row.deleteSize,
    height: row.deleteSize,
    p: 0,
    borderRadius: row.deleteRadius,
    color: row.deleteColor,
    flexShrink: 0,
    "&:hover": { bgcolor: row.deleteHoverBg, color: accent },
    "&:focus-visible": {
      outline: `2px solid ${inkfree.main}`,
      outlineOffset: "2px",
    },
  } as SxProps<Theme>,

  rowDeleteIcon: {
    width: row.deleteIconSize,
    height: row.deleteIconSize,
  } as SxProps<Theme>,

  // ----------------------------------------------------- security sub-rows

  codeEditor: {
    display: "flex",
    alignItems: "center",
    gap: security.codeGap,
    m: security.blockMargin,
    p: security.codePadding,
    borderRadius: security.codeRadius,
    bgcolor: security.codeBg,
    border: `1px solid ${security.codeBorder}`,
    width: "fit-content",
    maxWidth: "100%",
  } as SxProps<Theme>,

  codeIcon: {
    width: security.codeIconSize,
    height: security.codeIconSize,
    color: accent,
    flexShrink: 0,
  } as SxProps<Theme>,

  codeLabel: {
    fontSize: security.codeLabelFontSize,
    fontWeight: security.codeLabelFontWeight,
    color: security.codeLabelColor,
    lineHeight: "normal",
    whiteSpace: "nowrap",
  } as SxProps<Theme>,

  codeInput: {
    width: security.codeInputWidth,
    maxWidth: "100%",
    "& .MuiInputBase-root": {
      height: security.codeInputHeight,
      bgcolor: "transparent",
      borderBottom: security.codeInputUnderline,
      "&:before, &:after": { display: "none" },
      "&:hover": { borderBottom: security.codeInputUnderline },
      "&.Mui-focused": { borderBottom: input.focusUnderline },
    },
    "& .MuiInputBase-input": {
      p: input.padding,
      height: "auto",
      fontSize: security.codeInputFontSize,
      fontWeight: security.codeInputFontWeight,
      lineHeight: "normal",
      color: input.color,
      "&::placeholder": { color: input.placeholderColor, opacity: 1 },
    },
  } as SxProps<Theme>,

  codeSave: (enabled: boolean) =>
    ({
      height: security.saveHeight,
      minWidth: 0,
      p: security.savePadding,
      borderRadius: security.saveRadius,
      background: enabled ? ctaBg : security.saveDisabledBg,
      color: white,
      fontSize: security.saveFontSize,
      fontWeight: security.saveFontWeight,
      lineHeight: "normal",
      textTransform: "none",
      whiteSpace: "nowrap",
      boxShadow: "none",
      flexShrink: 0,
      "&:hover": {
        background: enabled ? ctaBgHover : security.saveDisabledBg,
        boxShadow: "none",
      },
      "&.Mui-disabled": { background: security.saveDisabledBg, color: white },
    }) as SxProps<Theme>,

  codeSaved: {
    display: "flex",
    alignItems: "center",
    gap: security.codeGap,
    m: security.blockMargin,
    p: security.savedPadding,
    borderRadius: security.codeRadius,
    bgcolor: security.savedBg,
    border: `1px solid ${security.savedBorder}`,
    width: "fit-content",
    maxWidth: "100%",
  } as SxProps<Theme>,

  codeSavedTick: {
    width: security.codeIconSize,
    height: security.codeIconSize,
    color: security.savedTickColor,
    flexShrink: 0,
  } as SxProps<Theme>,

  codeMasked: {
    fontSize: security.maskedFontSize,
    fontWeight: security.maskedFontWeight,
    color: security.maskedColor,
    letterSpacing: security.maskedLetterSpacing,
    lineHeight: "normal",
  } as SxProps<Theme>,

  codeEdit: {
    p: 0,
    minWidth: 0,
    fontSize: security.editFontSize,
    fontWeight: security.editFontWeight,
    color: accent,
    lineHeight: "normal",
    textTransform: "none",
    "&:hover": { bgcolor: "transparent", color: accentHover },
    ...inlineControlRing(14),
  } as SxProps<Theme>,

  otpNote: {
    display: "flex",
    alignItems: "center",
    gap: security.noteGap,
    m: security.noteMargin,
    color: security.noteColor,
  } as SxProps<Theme>,

  otpNoteIcon: {
    width: security.noteIconSize,
    height: security.noteIconSize,
    flexShrink: 0,
  } as SxProps<Theme>,

  otpNoteText: {
    fontSize: security.noteFontSize,
    fontWeight: security.noteFontWeight,
    lineHeight: 1.45,
  } as SxProps<Theme>,

  // ----------------------------------------------------- SMS delivery channels

  /**
   * Row 2 of the signer card: "Deliver via" (left) and the role /
   * delivery-mode / security selects (right) — the design's "routing
   * decisions live together" line.
   */
  rowSettingsLine: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: delivery.lineGap,
    p: delivery.linePadding,
    mt: "6px",
  } as SxProps<Theme>,

  /** "Deliver via [ ] Email …" cluster, left side of `rowSettingsLine`. */
  deliverViaLine: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: delivery.lineGap,
  } as SxProps<Theme>,

  /** Role / delivery-mode / security selects, right side of `rowSettingsLine`. */
  rowSettingsRight: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: row.settingsRightGap,
    ml: "auto",
    mr: `${row.deleteSize}px`,
  } as SxProps<Theme>,

  deliverViaLabel: {
    fontSize: delivery.labelFontSize,
    letterSpacing: delivery.labelLetterSpacing,
    textTransform: "uppercase",
    color: delivery.labelColor,
    fontWeight: delivery.labelFontWeight,
    whiteSpace: "nowrap",
  } as SxProps<Theme>,

  deliverOption: {
    display: "flex",
    alignItems: "center",
    gap: delivery.optionGap,
    p: 0,
    minWidth: 0,
    textTransform: "none",
    lineHeight: "normal",
    "&:hover": { bgcolor: "transparent" },
    "&.Mui-disabled": { opacity: 0.55 },
    ...inlineControlRing(delivery.boxSize),
  } as SxProps<Theme>,

  deliverLock: {
    fontSize: delivery.optionFontSize,
    color: "text.disabled",
    ml: "2px",
    flexShrink: 0,
  } as SxProps<Theme>,

  deliverBox: (on: boolean) =>
    ({
      width: delivery.boxSize,
      height: delivery.boxSize,
      borderRadius: delivery.boxRadius,
      border: `${delivery.boxBorderWidth} solid ${
        on ? delivery.boxBorderOn : delivery.boxBorderOff
      }`,
      bgcolor: on ? delivery.boxBgOn : delivery.boxBgOff,
      color: white,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxSizing: "border-box",
      flexShrink: 0,
    }) as SxProps<Theme>,

  deliverTick: (on: boolean) =>
    ({
      width: delivery.tickSize,
      height: delivery.tickSize,
      opacity: on ? 1 : 0,
    }) as SxProps<Theme>,

  deliverOptionLabel: {
    fontSize: delivery.optionFontSize,
    fontWeight: delivery.optionFontWeight,
    color: delivery.optionColor,
    lineHeight: "normal",
    whiteSpace: "nowrap",
  } as SxProps<Theme>,

  /** "(US and Canada only)" beside a channel label. */
  deliverOptionNote: {
    ml: "5px",
    fontSize: delivery.optionNoteFontSize,
    fontWeight: delivery.optionNoteFontWeight,
    color: delivery.optionNoteColor,
    lineHeight: "normal",
    whiteSpace: "nowrap",
  } as SxProps<Theme>,

  /**
   * Inline delivery-phone in the row (shown for SMS / WhatsApp). The control is
   * the shared `CustomContactNumberInput`; this only sets its width and clips
   * the field's helper text so the row height stays fixed.
   */
  phoneInlineWrap: {
    flex: delivery.inlineFlex,
    minWidth: delivery.inlineMinWidth,
    "& .MuiFormHelperText-root": { display: "none" },
  } as SxProps<Theme>,

  phoneIcon: {
    width: delivery.iconSize,
    height: delivery.iconSize,
    color: delivery.iconColor,
    flexShrink: 0,
  } as SxProps<Theme>,

  phoneTitle: {
    fontSize: delivery.titleFontSize,
    fontWeight: delivery.titleFontWeight,
    color: delivery.titleColor,
    lineHeight: "normal",
    whiteSpace: "nowrap",
  } as SxProps<Theme>,

  /** Phone-authentication warm banner note + "Same as delivery number" chip. */
  authRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: security.codeGap,
    m: delivery.boxMargin,
    p: delivery.authPadding,
    borderRadius: delivery.boxRadius2,
    bgcolor: delivery.boxBg,
    border: `1px solid ${delivery.boxBorder}`,
    width: "fit-content",
    maxWidth: "100%",
  } as SxProps<Theme>,

  authNote: {
    fontSize: delivery.authNoteFontSize,
    color: delivery.authNoteColor,
    whiteSpace: "nowrap",
  } as SxProps<Theme>,

  /** Shared phone control inside the auth banner. */
  authPhoneWrap: {
    minWidth: delivery.authPhoneMinWidth,
    "& .MuiFormHelperText-root": { display: "none" },
  } as SxProps<Theme>,

  sameAsDelivery: {
    display: "flex",
    alignItems: "center",
    gap: delivery.sameGap,
    minWidth: 0,
    height: delivery.sameHeight,
    p: delivery.samePadding,
    borderRadius: delivery.sameRadius,
    border: delivery.sameBorder,
    bgcolor: white,
    color: delivery.sameColor,
    fontSize: delivery.sameFontSize,
    fontWeight: delivery.sameFontWeight,
    lineHeight: "normal",
    textTransform: "none",
    whiteSpace: "nowrap",
    flexShrink: 0,
    "&:hover": { bgcolor: row.deleteHoverBg },
  } as SxProps<Theme>,

  sameAsDeliveryIcon: {
    width: delivery.sameIconSize,
    height: delivery.sameIconSize,
  } as SxProps<Theme>,

  // -------------------------------------------------------------- delay slot

  delaySlot: {
    display: "flex",
    alignItems: "center",
    gap: delay.gap,
    m: delay.margin,
    pl: delay.paddingLeft,
    width: "100%",
  } as SxProps<Theme>,

  delayStem: {
    width: delay.stemWidth,
    height: delay.stemHeight,
    ml: "1px",
    bgcolor: delay.stemColor,
    flexShrink: 0,
  } as SxProps<Theme>,

  delayPill: {
    display: "flex",
    alignItems: "center",
    gap: delay.pillGap,
    height: delay.pillHeight,
    p: delay.pillPadding,
    borderRadius: delay.pillRadius,
    bgcolor: delay.pillBg,
    border: `1px solid ${delay.pillBorder}`,
  } as SxProps<Theme>,

  delayClock: {
    width: delay.clockSize,
    height: delay.clockSize,
    color: accent,
    flexShrink: 0,
  } as SxProps<Theme>,

  delayText: {
    fontSize: delay.textFontSize,
    fontWeight: delay.textFontWeight,
    color: delay.textColor,
    lineHeight: "normal",
    whiteSpace: "nowrap",
  } as SxProps<Theme>,

  delaySteps: {
    display: "flex",
    gap: delay.stepGap,
  } as SxProps<Theme>,

  delayStep: {
    width: delay.stepSize,
    height: delay.stepSize,
    minWidth: 0,
    p: 0,
    borderRadius: delay.stepRadius,
    bgcolor: row.bg,
    border: `1px solid ${delay.stepBorder}`,
    color: accent,
    fontSize: delay.stepFontSize,
    fontWeight: delay.stepFontWeight,
    lineHeight: 1,
    userSelect: "none",
    "&:hover": { bgcolor: delay.stepHoverBg, borderColor: delay.stepBorder },
  } as SxProps<Theme>,

  delayRemove: {
    width: delay.stepSize,
    height: delay.stepSize,
    p: 0,
    borderRadius: delay.stepRadius,
    color: row.deleteColor,
    "&:hover": { bgcolor: delay.stepHoverBg, color: accent },
  } as SxProps<Theme>,

  delayRemoveIcon: {
    width: delay.removeIconSize,
    height: delay.removeIconSize,
  } as SxProps<Theme>,

  delayAdd: {
    display: "flex",
    alignItems: "center",
    gap: delay.addGap,
    minWidth: 0,
    height: delay.addHeight,
    p: delay.addPadding,
    borderRadius: delay.addRadius,
    border: delay.addBorder,
    bgcolor: row.bg,
    color: delay.addColor,
    fontSize: delay.addFontSize,
    fontWeight: delay.addFontWeight,
    lineHeight: "normal",
    textTransform: "none",
    whiteSpace: "nowrap",
    "&:hover": { bgcolor: row.bg, borderColor: accent, color: accent },
  } as SxProps<Theme>,

  delayAddIcon: {
    width: delay.addIconSize,
    height: delay.addIconSize,
  } as SxProps<Theme>,

  // ---------------------------------------------------------- drop / empty

  /** Wraps a level so it can highlight while a signer hovers over it. */
  levelGroup: (dropActive: boolean) =>
    ({
      display: "flex",
      flexDirection: "column",
      borderRadius: levelDropTarget.radius,
      outline: dropActive ? levelDropTarget.outline : "none",
      outlineOffset: levelDropTarget.outlineOffset,
      bgcolor: dropActive ? levelDropTarget.bg : "transparent",
    }) as SxProps<Theme>,

  newLevelZone: (active: boolean) =>
    ({
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: newLevelZone.height,
      mt: "4px",
      borderRadius: newLevelZone.radius,
      border: `${newLevelZone.borderWidth} dashed ${
        active ? newLevelZone.borderActive : newLevelZone.borderIdle
      }`,
      bgcolor: active ? newLevelZone.bgActive : newLevelZone.bgIdle,
      color: active ? newLevelZone.colorActive : newLevelZone.colorIdle,
      fontSize: newLevelZone.fontSize,
      fontWeight: newLevelZone.fontWeight,
      lineHeight: "normal",
      boxSizing: "border-box",
      width: "100%",
    }) as SxProps<Theme>,

  emptyBlock: {
    border: empty.border,
    borderRadius: empty.radius,
    p: empty.padding,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: empty.gap,
    width: "100%",
    boxSizing: "border-box",
  } as SxProps<Theme>,

  emptyTitle: {
    fontSize: empty.titleFontSize,
    fontWeight: empty.titleFontWeight,
    color: empty.titleColor,
    lineHeight: "normal",
    textAlign: "center",
  } as SxProps<Theme>,

  emptyButton: {
    height: empty.buttonHeight,
    minWidth: 0,
    p: empty.buttonPadding,
    borderRadius: empty.buttonRadius,
    background: ctaBg,
    color: white,
    fontSize: empty.buttonFontSize,
    fontWeight: empty.buttonFontWeight,
    lineHeight: "normal",
    textTransform: "none",
    whiteSpace: "nowrap",
    boxShadow: "none",
    "&:hover": { background: ctaBgHover, boxShadow: "none" },
  } as SxProps<Theme>,

  // ------------------------------------------------------------- host email

  /** Single-row pill — icon, label, info glyph, inline input. */
  hostEmailBanner: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: hostEmail.gap,
    p: hostEmail.padding,
    borderRadius: hostEmail.radius,
    bgcolor: hostEmail.bg,
    border: `1px solid ${hostEmail.border}`,
    mb: hostEmail.marginBottom,
    width: "fit-content",
    maxWidth: "100%",
    boxSizing: "border-box",
  } as SxProps<Theme>,

  hostEmailIcon: {
    width: hostEmail.iconSize,
    height: hostEmail.iconSize,
    color: hostEmail.iconColor,
    flexShrink: 0,
  } as SxProps<Theme>,

  hostEmailLabel: {
    fontSize: hostEmail.labelFontSize,
    fontWeight: hostEmail.labelFontWeight,
    color: hostEmail.labelColor,
    lineHeight: "normal",
    whiteSpace: "nowrap",
  } as SxProps<Theme>,

  hostEmailInfoIcon: {
    width: hostEmail.infoIconSize,
    height: hostEmail.infoIconSize,
    color: hostEmail.infoIconColor,
    flexShrink: 0,
    cursor: "help",
  } as SxProps<Theme>,

  hostEmailField: {
    width: hostEmail.fieldWidth,
    "& .MuiOutlinedInput-root": {
      minHeight: hostEmail.fieldHeight,
      borderRadius: "6px",
      bgcolor: white,
    },
    "& .MuiOutlinedInput-input": {
      p: "5px 8px",
      fontSize: hostEmail.labelFontSize,
    },
  } as SxProps<Theme>,

  // ------------------------------------------------------------- sms credits

  /**
   * Credit callout above the signer list. Full width rather than
   * `hostEmailBanner`'s hugging pill — it carries a sentence and an action,
   * and shrinking to fit would put the buy link somewhere different on every
   * request.
   */
  smsCreditBanner: (blocked: boolean) =>
    ({
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: smsCredit.gap,
      p: smsCredit.padding,
      borderRadius: smsCredit.radius,
      mb: smsCredit.marginBottom,
      bgcolor: blocked ? smsCredit.blocked.bg : smsCredit.low.bg,
      border: `1px solid ${
        blocked ? smsCredit.blocked.border : smsCredit.low.border
      }`,
      boxSizing: "border-box",
    }) as SxProps<Theme>,

  smsCreditIcon: (blocked: boolean) =>
    ({
      width: smsCredit.iconSize,
      height: smsCredit.iconSize,
      color: blocked ? smsCredit.blocked.icon : smsCredit.low.icon,
      flexShrink: 0,
    }) as SxProps<Theme>,

  smsCreditText: (blocked: boolean) =>
    ({
      flex: 1,
      minWidth: 0,
      fontSize: smsCredit.textFontSize,
      fontWeight: smsCredit.textFontWeight,
      color: blocked ? smsCredit.blocked.text : smsCredit.low.text,
      lineHeight: 1.45,
    }) as SxProps<Theme>,

  smsCreditLink: {
    flexShrink: 0,
    fontSize: smsCredit.linkFontSize,
    fontWeight: smsCredit.linkFontWeight,
    color: accent,
    textDecoration: "underline",
    cursor: "pointer",
    background: "none",
    border: 0,
    p: 0,
    "&:hover": { color: accentHover },
  } as SxProps<Theme>,

  // -------------------------------------------------------------- approvers

  approversStack: {
    display: "flex",
    flexDirection: "column",
    gap: approvers.stackGap,
    width: "100%",
  } as SxProps<Theme>,

  approversBanner: {
    display: "flex",
    alignItems: "center",
    gap: approvers.bannerGap,
    p: approvers.bannerPadding,
    borderRadius: approvers.bannerRadius,
    bgcolor: approvers.bannerBg,
    mb: approvers.bannerMarginBottom,
  } as SxProps<Theme>,

  approversBannerIcon: {
    width: approvers.bannerIconSize,
    height: approvers.bannerIconSize,
    color: approvers.bannerColor,
    flexShrink: 0,
  } as SxProps<Theme>,

  approversBannerText: {
    fontSize: approvers.bannerFontSize,
    fontWeight: approvers.bannerFontWeight,
    color: approvers.bannerColor,
    lineHeight: 1.45,
  } as SxProps<Theme>,

  approverCard: {
    display: "flex",
    flexDirection: "column",
    border: `1px solid ${row.border}`,
    borderRadius: row.radius,
    bgcolor: row.bg,
    boxShadow: row.shadow,
    width: "100%",
  } as SxProps<Theme>,

  approverRow: {
    display: "flex",
    alignItems: "center",
    gap: row.gap,
    minHeight: row.minHeight,
    pr: row.paddingRight,
  } as SxProps<Theme>,

  approverBar: {
    width: row.barWidth,
    alignSelf: "stretch",
    borderRadius: row.barRadius,
    bgcolor: approvers.barColor,
    flexShrink: 0,
  } as SxProps<Theme>,

  approverBadge: {
    width: approvers.badgeSize,
    height: approvers.badgeSize,
    borderRadius: "50%",
    bgcolor: approvers.badgeBg,
    color: approvers.badgeColor,
    fontSize: approvers.badgeFontSize,
    fontWeight: approvers.badgeFontWeight,
    lineHeight: "normal",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  } as SxProps<Theme>,

  approverTypeIcon: {
    width: approvers.typeIconSize,
    height: approvers.typeIconSize,
    color: approvers.typeIconColor,
    flexShrink: 0,
  } as SxProps<Theme>,

  approverRule: {
    display: "flex",
    alignItems: "center",
    gap: approvers.ruleGap,
    minWidth: 0,
    height: approvers.ruleHeight,
    p: approvers.rulePadding,
    borderRadius: approvers.ruleRadius,
    bgcolor: approvers.ruleBg,
    color: approvers.ruleColor,
    fontSize: approvers.ruleFontSize,
    fontWeight: approvers.ruleFontWeight,
    lineHeight: "normal",
    textTransform: "none",
    whiteSpace: "nowrap",
    flexShrink: 0,
    "&:hover": { bgcolor: approvers.ruleHoverBg },
  } as SxProps<Theme>,

  approverRuleDot: {
    width: approvers.ruleDotSize,
    height: approvers.ruleDotSize,
    borderRadius: "50%",
    bgcolor: accent,
    flexShrink: 0,
  } as SxProps<Theme>,

  membersBlock: {
    display: "flex",
    flexDirection: "column",
    gap: approvers.membersGap,
    p: approvers.membersPadding,
    borderTop: `1px solid ${approvers.membersBorderTop}`,
    mt: "2px",
  } as SxProps<Theme>,

  memberRow: {
    display: "flex",
    alignItems: "center",
    gap: approvers.memberGap,
    mt: approvers.memberMarginTop,
  } as SxProps<Theme>,

  memberBullet: {
    width: approvers.bulletSize,
    height: approvers.bulletSize,
    borderRadius: "50%",
    bgcolor: approvers.bulletColor,
    flexShrink: 0,
  } as SxProps<Theme>,

  memberRemove: {
    width: approvers.memberRemoveSize,
    height: approvers.memberRemoveSize,
    p: 0,
    borderRadius: approvers.memberRemoveRadius,
    color: row.deleteColor,
    flexShrink: 0,
    "&:hover": { bgcolor: row.deleteHoverBg, color: accent },
    "&:focus-visible": {
      outline: `2px solid ${inkfree.main}`,
      outlineOffset: "2px",
    },
  } as SxProps<Theme>,

  memberRemoveIcon: {
    width: approvers.memberRemoveIconSize,
    height: approvers.memberRemoveIconSize,
  } as SxProps<Theme>,

  addMember: {
    display: "flex",
    alignItems: "center",
    gap: approvers.addMemberGap,
    mt: approvers.addMemberMarginTop,
    p: 0,
    minWidth: 0,
    width: "fit-content",
    color: accent,
    fontSize: approvers.addMemberFontSize,
    fontWeight: approvers.addMemberFontWeight,
    lineHeight: "normal",
    textTransform: "none",
    "&:hover": { bgcolor: "transparent", color: accentHover },
    ...inlineControlRing(15),
  } as SxProps<Theme>,

  addMemberIcon: {
    width: approvers.addMemberIconSize,
    height: approvers.addMemberIconSize,
  } as SxProps<Theme>,

  visuallyHidden: {
    position: "absolute",
    width: "1px",
    height: "1px",
    p: 0,
    m: "-1px",
    overflow: "hidden",
    clip: "rect(0 0 0 0)",
    whiteSpace: "nowrap",
    border: 0,
  } as SxProps<Theme>,
};

export default approversAndSignersStyles;
